import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, MapPin, HandHeart, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Flag from '../components/world/Flag';
import { useAuth } from '../context/AuthContext';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';

/**
 * Vzájemná podpora v jedné zemi.
 *
 * Česká stránka dělí nabídky na Česko a „Svět" — jednu přihrádku pro celý
 * zbytek planety. Tady je to po zemích: kdo hledá doprovod k lékaři v Bangkoku,
 * nepotřebuje vidět nabídky z Brazílie.
 *
 * Nabídky bez země zůstávají tam, kde byly. Tahle stránka pracuje jen
 * s `country_iso2`, takže se česká stránka nemění.
 */
export default function CountrySupportPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [parametry, setParametry] = useSearchParams();
  const { t, datum, dvojjazycne } = useJazyk();
  const { user } = useAuth();

  const [zeme, setZeme] = useState(null);
  const [nabidky, setNabidky] = useState([]);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  const [otevren, setOtevren] = useState(false);

  const druh = parametry.get('druh') || 'vse';   // vse | offer | request
  const hledani = parametry.get('q') || '';
  const [hledaniVstup, setHledaniVstup] = useState(hledani);

  useEffect(() => { setHledaniVstup(hledani); }, [hledani]);

  const zmenParametr = useCallback((zmeny) => {
    const nove = new URLSearchParams(parametry);
    Object.entries(zmeny).forEach(([k, v]) => {
      if (!v) nove.delete(k); else nove.set(k, String(v));
    });
    setParametry(nove, { replace: false });
  }, [parametry, setParametry]);

  const nacti = useCallback(async () => {
    setNacita(true);
    setChyba('');
    try {
      const z = await axios.get(`${API}/geo/countries/${slug}`);
      setZeme(z.data);
      const { data } = await axios.get(`${API}/services`, {
        params: { country_iso2: z.data.iso2, search: hledani || undefined },
      });
      setNabidky(data || []);
    } catch (e) {
      setChyba(e?.response?.status === 404 ? 'neznamaZeme' : 'chyba');
    } finally {
      setNacita(false);
    }
  }, [slug, hledani]);

  useEffect(() => { nacti(); }, [nacti]);

  const videt = useMemo(() => (
    druh === 'vse' ? nabidky : nabidky.filter((n) => (n.post_type || 'offer') === druh)
  ), [nabidky, druh]);

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">
          {chyba === 'neznamaZeme' ? t('zeme.neznama') : t('vypis.chyba')}
        </p>
        <button type="button" onClick={() => navigate('/world')}
                className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
        </button>
      </div>
    );
  }

  const nazevZeme = dvojjazycne(zeme?.name_cs, zeme?.name_en);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="country-support">
      <button type="button" onClick={() => navigate(`/world/${slug}`)}
              className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
        <ArrowLeft className="w-4 h-4" /> {t('detail.zpetNa', { zeme: nazevZeme })}
      </button>

      <header className="mt-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {zeme && <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />}
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-bloom-text">
              {t('sekce.support')}
            </h1>
            <p className="text-bloom-sub text-sm">
              {nazevZeme}
              {videt.length > 0 && ` · ${t('vypis.zaznamu', { pocet: videt.length })}`}
            </p>
          </div>
        </div>

        {zeme && (
          <NovaNabidka zeme={zeme} user={user} otevren={otevren}
                       nastavOtevren={setOtevren} poUlozeni={nacti} />
        )}
      </header>

      <form className="mt-5"
            onSubmit={(e) => { e.preventDefault(); zmenParametr({ q: hledaniVstup }); }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
          <Input value={hledaniVstup} onChange={(e) => setHledaniVstup(e.target.value)}
                 placeholder={t('obecne.hledatPriklad')} className="pl-9"
                 aria-label={t('obecne.hledat')} data-testid="podpora-hledani" />
        </div>
      </form>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {[['vse', 'pod.vse'], ['offer', 'pod.nabidky'], ['request', 'pod.poptavky']].map(([v, klic]) => (
          <button key={v} type="button"
                  onClick={() => zmenParametr({ druh: v === 'vse' ? '' : v })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    druh === v ? 'bg-bloom-violet text-white border-bloom-violet'
                               : 'bg-white text-bloom-sub border-border/50 hover:border-bloom-violet/40'}`}>
            {t(klic)}
          </button>
        ))}
      </div>

      {nacita ? (
        <div className="py-16 flex justify-center"><div className="spinner" /></div>
      ) : videt.length === 0 ? (
        <div className="py-14 text-center">
          <p className="text-bloom-sub">
            {hledani || druh !== 'vse' ? t('pod.zadneNalezeny') : t('okoli.zadneNabidky')}
          </p>
          <p className="text-bloom-sub text-sm mt-1">
            {hledani || druh !== 'vse' ? t('pod.zkusteFiltry') : t('pod.vytvortePrvni')}
          </p>
        </div>
      ) : (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="podpora-seznam">
          {videt.map((n) => <li key={n.id}><Karta nabidka={n} datum={datum} /></li>)}
        </ul>
      )}
    </div>
  );
}

function Karta({ nabidka: n, datum }) {
  const { t } = useJazyk();
  const jePoptavka = (n.post_type || 'offer') === 'request';

  return (
    <div className="h-full bg-white border border-border/40 rounded-2xl p-4">
      <div className="flex items-center justify-between gap-2">
        <span className={`px-2 py-0.5 rounded-lg text-[11px] font-medium ${
          jePoptavka ? 'bg-bloom-pride-pink/10 text-bloom-pride-pink'
                     : 'bg-bloom-violet/10 text-bloom-violet'}`}>
          {t(jePoptavka ? 'pod.poptavka' : 'pod.nabidka')}
        </span>
        <span className="text-[11px] text-bloom-sub">{datum(n.created_at)}</span>
      </div>

      {n.offer && (
        <p className="mt-2 text-sm text-bloom-text font-medium">{t('pod.nabizimCo', { co: n.offer })}</p>
      )}
      {n.need && (
        <p className="mt-1 text-sm text-bloom-sub">{t('pod.hledamCo', { co: n.need })}</p>
      )}
      {n.description && (
        <p className="mt-2 text-sm text-bloom-sub leading-relaxed line-clamp-4">{n.description}</p>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-bloom-sub">
        <span>{t('pod.od', { kdo: n.username })}</span>
        {n.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />{n.location}
          </span>
        )}
      </div>
    </div>
  );
}

/** Nová nabídka nebo poptávka v téhle zemi. */
function NovaNabidka({ zeme, user, otevren, nastavOtevren, poUlozeni }) {
  const { t } = useJazyk();
  const [f, setF] = useState({ post_type: 'offer', offer: '', need: '', description: '', location: '' });
  const [odesila, setOdesila] = useState(false);

  // Bez ověřeného e-mailu tlačítko není, stejně jako na české stránce.
  if (!user?.email_verified) return null;

  const jePoptavka = f.post_type === 'request';

  const uloz = async (e) => {
    e.preventDefault();
    if (odesila) return;
    setOdesila(true);
    try {
      await axios.post(`${API}/services`, { ...f, country_iso2: zeme.iso2 });
      toast.success(t(jePoptavka ? 'pod.vytvorenoPoptavka' : 'pod.vytvorenoNabidka'));
      nastavOtevren(false);
      setF({ post_type: 'offer', offer: '', need: '', description: '', location: '' });
      poUlozeni();
    } catch {
      toast.error(t('pod.chybaVytvorit'));
    } finally {
      setOdesila(false);
    }
  };

  return (
    <Dialog open={otevren} onOpenChange={nastavOtevren}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-bloom-violet text-white shrink-0" data-testid="nova-nabidka">
          <Plus className="w-4 h-4 mr-1" />{t('pod.pridat')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-bloom-text flex items-center gap-2">
            <HandHeart className="w-4 h-4 text-bloom-violet" />
            {t(jePoptavka ? 'pod.novaPoptavka' : 'pod.novaNabidka')}
          </DialogTitle>
          <DialogDescription>
            {t(jePoptavka ? 'pod.popisPoptavky' : 'pod.popisNabidky')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={uloz} className="space-y-3">
          <div>
            <Label className="text-bloom-text text-sm">{t('pod.typPrispevku')}</Label>
            <Select value={f.post_type}
                    onValueChange={(v) => setF((p) => ({ ...p, post_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="offer">{t('pod.nabidka')}</SelectItem>
                <SelectItem value="request">{t('pod.poptavka')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-bloom-text text-sm">
              {t(jePoptavka ? 'pod.coNabizim' : 'pod.coNabizimP')}
            </Label>
            <Input value={f.offer} required={!jePoptavka} placeholder={t('pod.priklOffer')}
                   onChange={(e) => setF((p) => ({ ...p, offer: e.target.value }))}
                   data-testid="nabidka-offer" />
          </div>

          <div>
            <Label className="text-bloom-text text-sm">
              {t(jePoptavka ? 'pod.coHledamP' : 'pod.coHledamVymenou')}
            </Label>
            <Input value={f.need} required={jePoptavka} placeholder={t('pod.priklNeed')}
                   onChange={(e) => setF((p) => ({ ...p, need: e.target.value }))}
                   data-testid="nabidka-need" />
          </div>

          <div>
            <Label className="text-bloom-text text-sm">{t('pod.popis')}</Label>
            <Textarea value={f.description} rows={3} placeholder={t('pod.popisPriklad')}
                      onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))}
                      data-testid="nabidka-popis" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-bloom-text text-sm">{t('pod.lokalita')}</Label>
              <Input value={f.location} placeholder={t('odb.mesto').replace(' *', '')}
                     onChange={(e) => setF((p) => ({ ...p, location: e.target.value }))}
                     data-testid="nabidka-misto" />
            </div>
            <div>
              <Label className="text-bloom-text text-sm">{t('odb.stat')}</Label>
              {/* Stát se nevybírá – je daný stránkou, na které formulář stojí. */}
              <div className="h-9 flex items-center gap-2 px-3 rounded-md border border-input
                              bg-muted/40 text-sm">
                <Flag emoji={zeme.flag} iso2={zeme.iso2} size="sm" />
                {zeme.name_cs || zeme.name_en}
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-bloom-violet text-white" disabled={odesila}>
            {odesila ? t('odb.odesilam')
                     : t(jePoptavka ? 'pod.pridatPoptavku' : 'pod.pridatNabidku')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
